import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import AttachmentsIcon from '@mui/icons-material/AttachFile';
import Attachments from './Dialogs/Attachments';
import { isPersistedId } from '../../../helpers/idHelper';
import { useRecordIdForFetch } from '../../../helpers/useTour';

export default function AttachmentButton(props) {
    const { objectType, finalDocuments, sx, reloadParent } = props;
    // Attachments are real documents on the real record — a tour never
    // simulates them, so keep fetching against the record the user has open
    // rather than the mock id the tour swapped into formData.
    const recordId = useRecordIdForFetch(props.recordId);
    const { t } = useTranslation();

    const icon = {
        margin: 0,
        color: 'primary.main',
        minWidth: 0,
        ...sx
    };

    const [ open, setOpen ] = React.useState(false);
    const toggleDialog = () => setOpen(prev => !prev);

    const [ count, setCount ] = React.useState(0);
    // Same reasoning as recordId: a tour's simulated record carries simulated
    // final-document ids. They can't be cast, and they aren't this record's
    // documents anyway, so they never go out. A no-op on real records.
    const finalDocumentsKey = React.useMemo(
        () => JSON.stringify((finalDocuments ?? []).filter(isPersistedId)),
        [finalDocuments]
    );
    React.useEffect(() => {
        // Skip unsaved records and tour mocks — a mock id (and the mock final
        // documents that ride along with it) can't be cast to an ObjectId, so
        // the request would only 500.
        if(!isPersistedId(recordId) || open)
            return;
        const controller = new AbortController();
        (async () => {
            try {
                const data = { size: 1, offset: 0, finalDocuments: JSON.parse(finalDocumentsKey) };
                const result = await axios.post('/api/document/list/' + objectType + '/' + recordId, data, { signal: controller.signal });
                setCount((result.data.attachmentCount ?? 0) + (result.data.documentCount ?? 0));
            } catch(err) {
                if(!axios.isCancel(err))
                    console.error(err);
            }
        })();
        return () => controller.abort();
    }, [objectType, recordId, finalDocumentsKey, open]);

    return (
        <>
            <Tooltip title={count > 0 ? `${count} ${t('attachments')}` : t('attachments')}>
                <div data-guide="action-attachments">
                    <Button
                        disabled={false}
                        component={'div'}
                        onClick={toggleDialog}
                        sx={icon}
                        name='actionAttachments'
                    >
                        <Badge badgeContent={count} color="secondary" overlap="circular">
                            <AttachmentsIcon fontSize='medium' sx={{ color: 'primary.main' }}/>
                        </Badge>
                    </Button>
                </div>
            </Tooltip>
            {open &&
                <Attachments
                    finalDocuments={JSON.parse(finalDocumentsKey)}
                    objectType={objectType}
                    recordId={recordId}
                    toggleDialog={toggleDialog}
                    open={open}
                    reloadParent={reloadParent}
                />
            }
        </>
    );
}
